import React, { ComponentProps, useCallback, useContext, useEffect } from 'react';

import { useSnackbar } from 'notistack';
import { TourDetailsFormResources, useTourDetailsFormQuery } from '~/queries/tours/useTourDetailsFormQueries';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, AlertTitle, Box, Button, CircularProgress } from '@mui/material';

import CopyTourModal from '~/components/Tours/TourDetails/CopyTourModal';

import useToggleState from '~/hooks/useToggleState';

import ExtendedRJSForm from '../RJSF/ExtendedRJSForm';
import TourDetailsContext from '../TourDetailsContext';

interface Props {
  tourId: string;
}

export default function TourRootDetailsForm(props: Props) {
  const { tourId } = props;
  const { setTourDetails } = useContext(TourDetailsContext);
  const { enqueueSnackbar } = useSnackbar();

  const { isToggled: isModalOpen, toggle: toggleModal } = useToggleState(false);

  const { fetchRequestInstance, fetch, patchRequestInstance, patch } = useTourDetailsFormQuery(
    TourDetailsFormResources.ROOT,
  );

  // Refetch tour after copying
  const reloadTour = useCallback(() => fetch(tourId), [fetch, tourId]);

  useEffect(() => {
    fetch(tourId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  useEffect(() => {
    if (fetchRequestInstance.status === 'succeeded') {
      const { id, title, vendorId } = fetchRequestInstance.result.formData.tour;
      setTourDetails({ id, title, vendorId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRequestInstance]);

  const handleSubmission = useCallback<ComponentProps<typeof ExtendedRJSForm>['onSubmit']>(
    ({ formData }) => {
      patch(tourId, formData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tourId],
  );

  useEffect(() => {
    switch (patchRequestInstance.status) {
      case 'succeeded':
        enqueueSnackbar('Tour details updated successfully.', {
          variant: 'success',
        });
        break;
      case 'failed':
        enqueueSnackbar(`Submission failed! ${patchRequestInstance.error}`, {
          variant: 'error',
        });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchRequestInstance]);

  if (fetchRequestInstance.status === 'pending' || fetchRequestInstance.status === 'uninitiated') {
    return <CircularProgress />;
  }

  if (fetchRequestInstance.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              fetch(tourId);
            }}
          >
            Retry
          </Button>
        }
      >
        <AlertTitle>Failed to fetch the form.</AlertTitle>
        {fetchRequestInstance.error}
      </Alert>
    );
  }

  const { result } = fetchRequestInstance;

  const isSFTour = result.formData.tour.source === 'salesforce';

  return (
    <>
      {isSFTour && (
        <Box display="flex" flexDirection="row" justifyContent="end">
          <Button startIcon={<ContentCopyIcon />} variant="text" onClick={toggleModal}>
            Copy content
          </Button>
          <CopyTourModal tourId={tourId} open={isModalOpen} onClose={toggleModal} onSuccess={reloadTour} />
        </Box>
      )}
      <ExtendedRJSForm
        id="tour-details-form"
        schema={result.schema}
        uiSchema={result.uiSchema}
        formData={result.formData}
        onSubmit={handleSubmission}
        busy={patchRequestInstance.status === 'pending'}
      />
      {patchRequestInstance.status === 'failed' && <Alert severity="error">{patchRequestInstance.error}</Alert>}
    </>
  );
}
